// @catalog F.006
// FilterBar — controles compartidos por /ventas y /margenes (Bloque 6 plan
// dashboard_ux_ventas_margenes 2026-05-21). Autocomplete tienda + 2 DatePicker
// para rango + botón limpiar. Cambios persisten vía useLocalStorageState (B2).
import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es-mx";

export interface StoreOption {
  id: number | string;
  label: string;
}

export interface DateRange {
  // ISO YYYY-MM-DD; null cuando no hay filtro activo
  start: string | null;
  end: string | null;
}

interface FilterBarProps {
  stores: StoreOption[];
  selectedStore: StoreOption | null;
  dateRange: DateRange;
  onStoreChange: (next: StoreOption | null) => void;
  onDateRangeChange: (next: DateRange) => void;
  loading?: boolean | undefined;
  storeLabel?: string;
  // Habilita ocultar selector de tienda en pantallas que no lo necesitan
  hideStore?: boolean;
  // F1: si se provee, "Limpiar filtros" delega el reseteo al padre (ej. /ventas
  // resetea al período default en vez de dejar el rango en {null,null}
  // desincronizado). Sin onClear (ej. /margenes) se mantiene el reseteo previo.
  onClear?: () => void;
  // F2: granularidad del control de fecha. "month" → DatePicker mes/año (sin
  // selector de día); "day" → mes/día (comportamiento previo). Default "day"
  // para preservar /margenes, que no pasa esta prop.
  granularity?: "month" | "day";
}

function toDayjs(iso: string | null): Dayjs | null {
  if (!iso) return null;
  const d = dayjs(iso);
  return d.isValid() ? d : null;
}

function toIso(d: Dayjs | null): string | null {
  if (!d || !d.isValid()) return null;
  return d.format("YYYY-MM-DD");
}

export function FilterBar(props: FilterBarProps): JSX.Element {
  const {
    stores,
    selectedStore,
    dateRange,
    onStoreChange,
    onDateRangeChange,
    loading,
    storeLabel = "Tienda / vendedor",
    hideStore = false,
    onClear,
    granularity = "day",
  } = props;

  // F2: granularidad mensual oculta el selector de día y reetiqueta los controles.
  const isMonth = granularity === "month";
  const startLabel = isMonth ? "Mes desde" : "Desde";
  const endLabel = isMonth ? "Mes hasta" : "Hasta";
  const dateViews: Array<"year" | "month" | "day"> = isMonth
    ? ["year", "month"]
    : ["year", "month", "day"];

  // Estado local del rango: los DatePickers responden al instante, pero el
  // rango NO se aplica (ni dispara la query) hasta presionar "Aplicar". Así se
  // evita recargar con un rango a medias al editar solo "Desde".
  const [localRange, setLocalRange] = useState<DateRange>(dateRange);

  // Resincroniza el estado local cuando el rango externo cambia (ej. limpiar
  // filtros, restaurar desde localStorage).
  useEffect(() => {
    setLocalRange(dateRange);
  }, [dateRange.start, dateRange.end]);

  // Hay cambios de fecha sin aplicar todavía.
  const pendingDateChange =
    localRange.start !== dateRange.start || localRange.end !== dateRange.end;

  const handleStartChange = useCallback(
    (next: Dayjs | null) => {
      const d = next && isMonth ? next.startOf("month") : next;
      setLocalRange((prev) => ({ ...prev, start: toIso(d) }));
    },
    [isMonth],
  );

  const handleEndChange = useCallback(
    (next: Dayjs | null) => {
      const d = next && isMonth ? next.startOf("month") : next;
      setLocalRange((prev) => ({ ...prev, end: toIso(d) }));
    },
    [isMonth],
  );

  const handleApply = useCallback(() => {
    onDateRangeChange(localRange);
  }, [localRange, onDateRangeChange]);

  const handleClear = useCallback(() => {
    if (onClear) {
      // El padre decide el reseteo coherente (ej. volver al período default).
      // localRange se resincroniza vía el useEffect cuando dateRange cambie.
      onClear();
      return;
    }
    onStoreChange(null);
    setLocalRange({ start: null, end: null });
    onDateRangeChange({ start: null, end: null });
  }, [onClear, onDateRangeChange, onStoreChange]);

  const hasFilters =
    selectedStore !== null ||
    localRange.start !== null ||
    localRange.end !== null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {!hideStore && (
          <Autocomplete
            size="small"
            sx={{ minWidth: 240, flex: { xs: 1, md: "0 0 280px" } }}
            options={stores}
            value={selectedStore}
            onChange={(_, next) => onStoreChange(next)}
            disabled={loading === true}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label={storeLabel} variant="outlined" />
            )}
            aria-label={storeLabel}
          />
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <DatePicker
            label={startLabel}
            value={toDayjs(localRange.start)}
            onChange={handleStartChange}
            disabled={loading === true}
            views={dateViews}
            openTo={isMonth ? "month" : "day"}
            maxDate={dayjs()}
            referenceDate={dayjs()}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                id: "filter-date-start",
              },
            }}
          />
          <DatePicker
            label={endLabel}
            value={toDayjs(localRange.end)}
            onChange={handleEndChange}
            disabled={loading === true}
            views={dateViews}
            openTo={isMonth ? "month" : "day"}
            maxDate={dayjs()}
            referenceDate={dayjs()}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                id: "filter-date-end",
              },
            }}
            {...(toDayjs(localRange.start)
              ? { minDate: toDayjs(localRange.start) as Dayjs }
              : {})}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            startIcon={<SearchIcon />}
            disabled={!pendingDateChange || loading === true}
            aria-label="Aplicar rango de fechas"
          >
            Aplicar
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleClear}
            startIcon={<RestartAltIcon />}
            disabled={!hasFilters || loading}
            aria-label="Limpiar filtros"
          >
            Limpiar filtros
          </Button>
          {pendingDateChange && (
            <Typography variant="caption" color="warning.main">
              cambios sin aplicar
            </Typography>
          )}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
}
